// src/components/category/DoubleUnitsEditor.tsx
import React, { useEffect, useMemo, useState } from "react";
import { Unit, UnitDimension, UnitSystem } from "../../types/Unit";

interface DoubleUnitsEditorProps {
    units: Unit[];
    dimensions: UnitDimension[];
    systems: UnitSystem[];
    /** Selected unit ids (all of one dimension). */
    value: string[];
    onChange: (unitIds: string[]) => void;
    disabled?: boolean;
}

/**
 * Editor for a DOUBLE parameter's units. Shows the chosen units as a list with
 * explicit Remove buttons, plus an "Add unit" picker. All units share one
 * dimension (chosen up front). Unless the dimension allows multiple units per
 * system (e.g. currency), only one unit per system may be added.
 */
const DoubleUnitsEditor: React.FC<DoubleUnitsEditorProps> = ({
    units,
    dimensions,
    systems,
    value,
    onChange,
    disabled = false,
}) => {
    const unitById = useMemo(() => new Map(units.map(u => [u.id, u])), [units]);
    const systemById = useMemo(() => new Map(systems.map(s => [s.id, s])), [systems]);

    // Dimension is inferred from the current selection; empty until something is picked.
    const dimensionId = useMemo(() => {
        for (const id of value) {
            const u = unitById.get(id);
            if (u) return u.unitDimensionId;
        }
        return "";
    }, [value, unitById]);

    // When nothing is selected yet, remember which dimension the user picked so
    // the add-unit picker can render before any unit exists.
    const [pendingDimension, setPendingDimension] = useState("");
    const activeDimensionId = dimensionId || pendingDimension;

    // Only dimensions that actually have units are worth offering.
    const selectableDimensions = useMemo(() => {
        const withUnits = new Set(units.map(u => u.unitDimensionId));
        return dimensions.filter(d => withUnits.has(d.id));
    }, [units, dimensions]);

    const allowMultiple = useMemo(
        () => dimensions.find(d => d.id === activeDimensionId)?.allowMultiplePerSystem ?? false,
        [dimensions, activeDimensionId]
    );

    // A non-convertible dimension is a currency: the author does not pick specific
    // units here — every unit of the dimension is allowed and the actual currency
    // is chosen when creating a listing or searching. We just keep the selection
    // in sync with all of the dimension's units (as an anchor + fallback).
    const isCurrency = useMemo(
        () => dimensions.find(d => d.id === activeDimensionId)?.convertible === false,
        [dimensions, activeDimensionId]
    );

    const dimensionUnitIds = useMemo(
        () => units.filter(u => u.unitDimensionId === activeDimensionId).map(u => u.id),
        [units, activeDimensionId]
    );

    useEffect(() => {
        if (!isCurrency) return;
        const same =
            value.length === dimensionUnitIds.length &&
            dimensionUnitIds.every(id => value.includes(id));
        if (!same) onChange(dimensionUnitIds);
    }, [isCurrency, dimensionUnitIds, value, onChange]);

    // Selected units, in selection order, resolved to Unit objects.
    const selectedUnits = useMemo(
        () => value.map(id => unitById.get(id)).filter((u): u is Unit => !!u),
        [value, unitById]
    );

    // Systems that already have a selected unit (for one-per-system enforcement).
    const usedSystems = useMemo(
        () => new Set(selectedUnits.map(u => u.unitSystemId)),
        [selectedUnits]
    );

    // Units that can still be added: in this dimension, not already picked, and —
    // unless the dimension allows multiple per system — not in an already-used system.
    const addableUnits = useMemo(() => {
        if (!activeDimensionId) return [];
        return units
            .filter(u => u.unitDimensionId === activeDimensionId)
            .filter(u => !value.includes(u.id))
            .filter(u => allowMultiple || !usedSystems.has(u.unitSystemId));
    }, [units, activeDimensionId, value, allowMultiple, usedSystems]);

    const [unitToAdd, setUnitToAdd] = useState("");

    const handleDimensionChange = (newDimId: string) => {
        if (value.length > 0) {
            const ok = window.confirm(
                "Changing the dimension will clear the currently selected units. Continue?"
            );
            if (!ok) return;
        }
        onChange([]);
        setUnitToAdd("");
        setPendingDimension(newDimId);
    };

    const addUnit = () => {
        if (!unitToAdd) return;
        onChange([...value, unitToAdd]);
        setUnitToAdd("");
    };

    const removeUnit = (unitId: string) => {
        onChange(value.filter(id => id !== unitId));
    };

    const systemName = (systemId: string) => systemById.get(systemId)?.name ?? systemId;

    return (
        <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
            {/* Dimension */}
            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                <span style={{ fontSize: "0.85rem", minWidth: "5rem" }}>Dimension</span>
                <select
                    className="parameter-editor-input"
                    value={activeDimensionId}
                    onChange={(e) => handleDimensionChange(e.target.value)}
                    disabled={disabled}
                    style={{ flex: 1 }}
                >
                    <option value="">— select dimension —</option>
                    {selectableDimensions.map(d => (
                        <option key={d.id} value={d.id}>{d.name}</option>
                    ))}
                </select>
            </div>

            {/* Currency: no per-unit authoring — chosen at listing/search time */}
            {activeDimensionId && isCurrency && (
                <div style={{ fontSize: "0.85rem", color: "var(--text-secondary)" }}>
                    Currency parameter — the specific currency is chosen when creating a
                    listing or searching (default USD). All {dimensionUnitIds.length} currencies
                    in this dimension are available.
                </div>
            )}

            {/* Selected units, each removable */}
            {!isCurrency && selectedUnits.length > 0 && (
                <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: "0.25rem" }}>
                    {selectedUnits.map(u => (
                        <li
                            key={u.id}
                            style={{ display: "flex", alignItems: "center", gap: "0.5rem", padding: "0.2rem 0.4rem", background: "var(--surface-2, #f3f4f6)", borderRadius: "4px" }}
                        >
                            <span style={{ flex: 1, fontSize: "0.9rem" }}>
                                {u.name}
                                <span style={{ color: "var(--text-secondary)", marginLeft: "0.4rem", fontSize: "0.8rem" }}>
                                    ({systemName(u.unitSystemId)})
                                </span>
                            </span>
                            <button
                                type="button"
                                className="btn-secondary"
                                onClick={() => removeUnit(u.id)}
                                disabled={disabled}
                                aria-label={`Remove ${u.name}`}
                            >
                                Remove
                            </button>
                        </li>
                    ))}
                </ul>
            )}

            {/* Add a unit */}
            {activeDimensionId && !isCurrency && (
                <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                    <select
                        className="parameter-editor-input"
                        value={unitToAdd}
                        onChange={(e) => setUnitToAdd(e.target.value)}
                        disabled={disabled || addableUnits.length === 0}
                        style={{ flex: 1 }}
                    >
                        <option value="">
                            {addableUnits.length === 0 ? "— no more units to add —" : "— add a unit —"}
                        </option>
                        {addableUnits.map(u => (
                            <option key={u.id} value={u.id}>
                                {u.name} ({systemName(u.unitSystemId)})
                            </option>
                        ))}
                    </select>
                    <button
                        type="button"
                        onClick={addUnit}
                        disabled={disabled || !unitToAdd}
                    >
                        Add
                    </button>
                </div>
            )}

            {activeDimensionId && units.filter(u => u.unitDimensionId === activeDimensionId).length === 0 && (
                <div style={{ fontSize: "0.85rem", color: "var(--text-secondary)" }}>
                    No units defined for this dimension.
                </div>
            )}
        </div>
    );
};

export default DoubleUnitsEditor;
